/**
 * RevenueCat Service
 * 
 * Handles In-App Purchases for iOS and Android using RevenueCat SDK
 * This provides a unified API for both platforms
 * 
 * Note: Uses dynamic import to avoid build errors on web platform
 */

import { isNativePlatform, isIOS, isAndroid } from './platformService';
import { Browser } from '@capacitor/browser';

// Local types to avoid import issues on web
type CustomerInfo = any;
type PurchasesOfferings = any;
type PurchasesPackage = any;

// Dynamic SDK references
let Purchases: any = null;
let LOG_LEVEL: any = null;

// RevenueCat Public API Keys (safe to include in client code)
const REVENUECAT_IOS_API_KEY = 'appl_TfjBWkUFwhnPLFlDjgRSnZGTJyz';
const REVENUECAT_ANDROID_API_KEY = 'goog_dhaoMjYeJgPmVCSNuQvXmXUjIEV';

// Product identifiers matching App Store Connect and Google Play Console
export const PRODUCT_IDS = {
  // User Subscriptions
  standard_monthly: 'io.petius.sub.standard.monthly',
  standard_annual: 'io.petius.sub.standard.yearly',
  premium_monthly: 'io.petius.sub.premium.monthly',
  premium_annual: 'io.petius.sub.premium.yearly',
  ultimate_monthly: 'io.petius.sub.ultimate.monthly',
  ultimate_annual: 'io.petius.sub.ultimate.yearly',
  // Provider Subscription
  provider_annual: 'io.petius.sub.provider.yearly',
} as const;

// Package IDs from RevenueCat Dashboard (for fetching from Current Offering)
export const PACKAGE_IDS = {
  // User Subscriptions
  standard_monthly: 'standard_monthly',
  standard_annual: 'standard_annual',
  premium_monthly: 'premium_monthly',
  premium_annual: 'premium_annual',
  ultimate_monthly: 'ultimate_monthly',
  ultimate_annual: 'ultimate_annual',
  // Provider Subscription
  provider_annual: 'service_providers_annual',
} as const;

// Credit Package Product IDs (Consumables) - Store IDs
export const CREDIT_PRODUCT_IDS = {
  starter: 'io.petius.consumable.credits.10',
  basic: 'io.petius.consumable.credits.25',
  premium: 'io.petius.consumable.credits.50',
  ultimate: 'io.petius.consumable.credits.100',
} as const;

// Credit Package IDs from RevenueCat Dashboard
export const CREDIT_PACKAGE_IDS = {
  starter: 'petmatch_10_credits',
  basic: 'petmatch_25_credits',
  premium: 'petmatch_50_credits',
  ultimate: 'petmatch_100_credits',
} as const;

// Native prices (Apple/Google stores) - for display purposes
export const NATIVE_PRICES = {
  subscriptions: {
    standard: { monthly: 11.99, annual: 98.99 },
    premium: { monthly: 14.99, annual: 164.99 },
    ultimate: { monthly: 28.99, annual: 319.99 },
  },
  provider: { annual: 444.99 },
  credits: {
    starter: 21.99,
    basic: 47.99,
    premium: 71.99,
    ultimate: 128.99,
  },
} as const;

// Web prices (PayPal) - for display purposes
export const WEB_PRICES = {
  subscriptions: {
    standard: { monthly: 5.99, annual: 65.89 },
    premium: { monthly: 9.99, annual: 109.89 },
    ultimate: { monthly: 19.99, annual: 219.89 },
  },
  provider: { annual: 299, setup: 10 },
  credits: {
    starter: 14.99,
    basic: 32.99,
    premium: 49.99,
    ultimate: 89.99,
  },
} as const;

// Map subscription tiers to product identifiers
export const TIER_TO_PRODUCT: Record<string, { monthly: string; annual: string }> = {
  standard: {
    monthly: PRODUCT_IDS.standard_monthly,
    annual: PRODUCT_IDS.standard_annual,
  },
  premium: {
    monthly: PRODUCT_IDS.premium_monthly,
    annual: PRODUCT_IDS.premium_annual,
  },
  ultimate: {
    monthly: PRODUCT_IDS.ultimate_monthly,
    annual: PRODUCT_IDS.ultimate_annual,
  },
};

// Entitlement identifiers in RevenueCat
export const ENTITLEMENTS = {
  standard: 'standard',
  premium: 'premium',
  ultimate: 'ultimate',
} as const;

// Cache for offerings (now stores ALL offerings, not just current)
let cachedOfferings: PurchasesOfferings | null = null;
let currentOffering: any | null = null;

let isInitialized = false;
let initializationError: Error | null = null;
let offeringsError: Error | null = null; // Separate from init error — SDK can be ready even if offerings fail
let isInitializing = false;

/**
 * Detect "already owned" errors from Google Play / Apple stores.
 * These indicate a consumable that wasn't properly consumed/acknowledged.
 */
const isAlreadyOwnedError = (error: any): boolean => {
  const message = (error?.message || error?.readableErrorCode || '').toLowerCase();
  const code = error?.code;
  // RevenueCat error codes for "product already owned"
  // Android: PRODUCT_ALREADY_PURCHASED / code 6
  // Also check common string patterns
  return (
    code === 6 ||
    message.includes('already owned') ||
    message.includes('already purchased') ||
    message.includes('product_already_purchased') ||
    message.includes('already_purchased')
  );
};

const normalizeRevenueCatError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  // Purchases SDK errors are often plain objects with detailed info
  try {
    const anyErr = error as any;

    // Build comprehensive error message
    const parts: string[] = [];

    if (anyErr?.message && typeof anyErr.message === 'string') {
      parts.push(anyErr.message);
    }

    if (anyErr?.code !== undefined) {
      parts.push(`[Code: ${anyErr.code}]`);
    }

    if (anyErr?.underlyingErrorMessage) {
      parts.push(`Underlying: ${anyErr.underlyingErrorMessage}`);
    }

    if (anyErr?.readableErrorCode) {
      parts.push(`RC Error: ${anyErr.readableErrorCode}`);
    }

    if (parts.length > 0) {
      return new Error(parts.join(' | '));
    }

    return new Error(JSON.stringify(error));
  } catch {
    return new Error('Unknown RevenueCat error');
  }
};

/**
 * Record an offerings-level error (does NOT affect SDK readiness).
 */
const recordOfferingsError = (error: unknown) => {
  offeringsError = normalizeRevenueCatError(error);
  cachedOfferings = null;
  currentOffering = null;
};

/**
 * Dynamically load RevenueCat SDK only on native platforms
 */
const loadRevenueCatSDK = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    return false;
  }

  if (Purchases) {
    return true;
  }

  try {
    const module = await import('@revenuecat/purchases-capacitor');
    Purchases = module.Purchases;
    LOG_LEVEL = module.LOG_LEVEL;
    return true;
  } catch (error) {
    console.error('RevenueCat: Failed to load SDK:', error);
    initializationError = error instanceof Error ? error : new Error('Failed to load SDK');
    return false;
  }
};

/**
 * Initialize RevenueCat SDK
 */
export const initializeRevenueCat = async (userId?: string): Promise<void> => {
  if (!isNativePlatform()) {
    console.log('RevenueCat: Not a native platform, skipping initialization');
    return;
  }

  if (isInitialized) {
    console.log('RevenueCat: Already initialized');
    return;
  }

  if (isInitializing) {
    console.log('RevenueCat: Initialization already in progress');
    let attempts = 0;
    while (isInitializing && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    return;
  }

  isInitializing = true;
  initializationError = null;
  offeringsError = null;

  console.log('RevenueCat: [STEP 1] Loading SDK...');
  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases) {
    isInitializing = false;
    const error = new Error('RevenueCat SDK not available - check bundling configuration');
    initializationError = error;
    console.error('RevenueCat: [STEP 1 FAILED] SDK not available');
    throw error;
  }
  console.log('RevenueCat: [STEP 1 SUCCESS] SDK loaded successfully');

  try {
    const apiKey = isIOS() ? REVENUECAT_IOS_API_KEY : REVENUECAT_ANDROID_API_KEY;
    const platform = isIOS() ? 'iOS' : 'Android';

    console.log(`RevenueCat: [STEP 2] Configuring for ${platform}`);
    console.log(`RevenueCat: API Key: ${apiKey.substring(0, 4)}...${apiKey.substring(apiKey.length - 4)}`);
    console.log(`RevenueCat: User ID: ${userId || 'anonymous'}`);

    console.log('RevenueCat: [STEP 2.1] Setting log level to DEBUG...');
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    console.log('RevenueCat: [STEP 2.1 SUCCESS] Log level set');

    console.log('RevenueCat: [STEP 2.2] Calling Purchases.configure...');
    await Purchases.configure({
      apiKey,
      appUserID: userId || null,
    });
    console.log('RevenueCat: [STEP 2.2 SUCCESS] Configure completed');

    isInitialized = true;
    console.log('RevenueCat: [STEP 3] Initialized successfully');

    // Sync pending purchases to finish any unacknowledged consumables
    try {
      console.log('RevenueCat: [STEP 3.1] Syncing pending purchases...');
      await Purchases.syncPurchases();
      console.log('RevenueCat: [STEP 3.1 SUCCESS] Purchases synced');
    } catch (syncError) {
      console.warn('RevenueCat: [STEP 3.1 WARNING] Sync failed (non-critical):', syncError);
    }

    // Fetch and cache ALL offerings
    try {
      console.log('RevenueCat: [STEP 4] Fetching offerings...');
      const offerings = await Purchases.getOfferings();
      console.log('RevenueCat: [STEP 4] Raw offerings response:', JSON.stringify(offerings, null, 2));

      cachedOfferings = offerings;
      currentOffering = offerings?.current || null;

      if (currentOffering) {
        console.log(`RevenueCat: [STEP 4 SUCCESS] Current offering: "${currentOffering.identifier}"`);
      } else {
        console.warn('RevenueCat: [STEP 4 WARNING] No current offering set in dashboard');
      }

      // Log ALL offerings
      if (offerings?.all) {
        const allOfferingKeys = Object.keys(offerings.all);
        console.log(`RevenueCat: [STEP 4] Total offerings available: ${allOfferingKeys.length}`);
        allOfferingKeys.forEach(key => {
          const offering = offerings.all[key];
          const packageCount = offering?.availablePackages?.length || 0;
          console.log(`RevenueCat: [STEP 4]   - "${key}": ${packageCount} packages`);
          if (packageCount > 0) {
            offering.availablePackages.forEach((pkg: any) => {
              console.log(`RevenueCat: [STEP 4]       • ${pkg.identifier} → ${pkg.product.identifier}`);
            });
          }
        });
      }
    } catch (offeringError: any) {
      console.error('RevenueCat: [STEP 4 FAILED] Failed to load offerings:', offeringError);

      // Log detailed error for credential issues
      if (offeringError?.code || offeringError?.underlyingErrorMessage) {
        console.error('RevenueCat: Error details:', {
          code: offeringError.code,
          message: offeringError.message,
          underlying: offeringError.underlyingErrorMessage,
          readableCode: offeringError.readableErrorCode,
        });
      }

      // Record the error but don't fail initialization
      // This allows the diagnostic tool to show the specific error
      recordOfferingsError(offeringError);
    }
  } catch (error) {
    console.error('RevenueCat: [INITIALIZATION FAILED]', error);
    initializationError = error instanceof Error ? error : new Error('Failed to initialize');
    isInitialized = false;
    throw error;
  } finally {
    isInitializing = false;
  }
};

/**
 * Set or update the user ID in RevenueCat
 */
export const setRevenueCatUserId = async (userId: string): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    return;
  }

  try {
    await Purchases.logIn({ appUserID: userId });
    console.log('RevenueCat: User ID set to', userId);
  } catch (error) {
    console.error('RevenueCat: Failed to set user ID', error);
    recordOfferingsError(error);
    throw error;
  }
};

/**
 * Log out the current user from RevenueCat
 */
export const logOutRevenueCat = async (): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    return;
  }

  try {
    await Purchases.logOut();
    console.log('RevenueCat: User logged out');
  } catch (error) {
    console.error('RevenueCat: Failed to log out', error);
    throw error;
  }
};

/**
 * Get available subscription offerings
 * Now returns the full offerings object including 'all' and 'current'
 */
export const getOfferings = async (): Promise<PurchasesOfferings | null> => {
  if (!isNativePlatform()) {
    return null;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    return null;
  }

  try {
    const result = await Purchases.getOfferings();
    console.log('RevenueCat: Offerings fetched:', {
      current: result?.current?.identifier,
      allCount: result?.all ? Object.keys(result.all).length : 0,
    });

    // Update cache
    cachedOfferings = result;
    currentOffering = result?.current || null;

    return result;
  } catch (error) {
    console.error('RevenueCat: Failed to get offerings', error);
    recordOfferingsError(error);
    throw error;
  }
};

/**
 * Get the current customer info including active subscriptions
 */
export const getCustomerInfo = async (): Promise<CustomerInfo | null> => {
  if (!isNativePlatform()) {
    return null;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    return null;
  }

  try {
    const result = await Purchases.getCustomerInfo();
    console.log('RevenueCat: Customer info fetched', result);
    return result.customerInfo;
  } catch (error) {
    console.error('RevenueCat: Failed to get customer info', error);
    recordOfferingsError(error);
    throw error;
  }
};

/**
 * Purchase a subscription package
 */
export const purchasePackage = async (pkg: PurchasesPackage): Promise<CustomerInfo | null> => {
  if (!isNativePlatform()) {
    throw new Error('RevenueCat not available on this platform');
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    throw new Error('RevenueCat not available on this platform');
  }

  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    console.log('RevenueCat: Purchase successful', result);
    return result.customerInfo;
  } catch (error: any) {
    if (error.userCancelled) {
      console.log('RevenueCat: User cancelled purchase');
      return null;
    }

    // Handle "already owned" error for consumables — sync and retry once
    if (isAlreadyOwnedError(error)) {
      console.log('RevenueCat: Product already owned, syncing to consume pending transaction...');
      try {
        await Purchases.syncPurchases();
        console.log('RevenueCat: Sync complete, retrying purchase...');
        const retryResult = await Purchases.purchasePackage({ aPackage: pkg });
        console.log('RevenueCat: Retry purchase successful', retryResult);
        return retryResult.customerInfo;
      } catch (retryError: any) {
        if (retryError.userCancelled) {
          console.log('RevenueCat: User cancelled retry purchase');
          return null;
        }
        console.error('RevenueCat: Retry purchase also failed', retryError);
        throw retryError;
      }
    }

    console.error('RevenueCat: Purchase failed', error);
    throw error;
  }
};

/**
 * Purchase a subscription by product ID
 */
export const purchaseProduct = async (productId: string): Promise<CustomerInfo | null> => {
  if (!isNativePlatform()) {
    throw new Error('RevenueCat not available on this platform');
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    throw new Error('RevenueCat not available on this platform');
  }

  try {
    const offerings = await getOfferings();
    if (!offerings?.current) {
      throw new Error('No offerings available');
    }

    const allPackages = offerings.current.availablePackages || [];
    const pkg = allPackages.find((p: any) => p.product.identifier === productId);

    if (!pkg) {
      throw new Error(`Product ${productId} not found in offerings`);
    }

    return await purchasePackage(pkg);
  } catch (error) {
    console.error('RevenueCat: Failed to purchase product', error);
    throw error;
  }
};

/**
 * Restore previous purchases
 */
export const restorePurchases = async (): Promise<CustomerInfo | null> => {
  if (!isNativePlatform()) {
    return null;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    return null;
  }

  try {
    const result = await Purchases.restorePurchases();
    console.log('RevenueCat: Purchases restored', result);
    return result.customerInfo;
  } catch (error) {
    console.error('RevenueCat: Failed to restore purchases', error);
    throw error;
  }
};

/**
 * Check if user has an active subscription
 */
export const hasActiveSubscription = async (): Promise<boolean> => {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) {
    return false;
  }

  const entitlements = customerInfo.entitlements.active;
  return Object.keys(entitlements).length > 0;
};

/**
 * Get the current subscription tier based on active entitlements
 */
export const getCurrentTier = async (): Promise<'free' | 'standard' | 'premium' | 'ultimate'> => {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) {
    return 'free';
  }

  const activeEntitlements = customerInfo.entitlements.active;

  // Check for Ultimate
  if (activeEntitlements[ENTITLEMENTS.ultimate]) {
    return 'ultimate';
  }

  // Check for Premium
  if (activeEntitlements[ENTITLEMENTS.premium]) {
    return 'premium';
  }

  // Check for Standard
  if (activeEntitlements[ENTITLEMENTS.standard]) {
    return 'standard';
  }

  return 'free';
};

/**
 * Get product ID for a tier and billing cycle
 */
export const getProductIdForTier = (
  tier: 'standard' | 'premium' | 'ultimate',
  billingCycle: 'monthly' | 'annual'
): string => {
  return TIER_TO_PRODUCT[tier][billingCycle];
};

/**
 * Purchase subscription by tier and billing cycle
 */
export const purchaseSubscription = async (
  tier: 'standard' | 'premium' | 'ultimate',
  billingCycle: 'monthly' | 'annual'
): Promise<CustomerInfo | null> => {
  const packageId = PACKAGE_IDS[`${tier}_${billingCycle}` as keyof typeof PACKAGE_IDS];
  if (packageId) {
    try {
      return await purchasePackageByIdentifier(packageId);
    } catch (error) {
      console.warn('RevenueCat: Package purchase failed, falling back to product ID', error);
    }
  }

  const productId = getProductIdForTier(tier, billingCycle);
  return await purchaseProduct(productId);
};

/**
 * Search for a package across ALL offerings (not just current)
 * This is the key fix for finding credit packages that may be in separate offerings
 */
export const findPackageAcrossAllOfferings = (packageId: string): { pkg: PurchasesPackage; offeringId: string } | null => {
  if (!cachedOfferings?.all) {
    console.warn('RevenueCat: No cached offerings available for search');
    return null;
  }

  const allOfferings = cachedOfferings.all;

  // First check current offering
  if (currentOffering?.availablePackages) {
    const pkg = currentOffering.availablePackages.find((p: any) => p.identifier === packageId);
    if (pkg) {
      console.log(`RevenueCat: Found package "${packageId}" in current offering "${currentOffering.identifier}"`);
      return { pkg, offeringId: currentOffering.identifier };
    }
  }

  // Then search ALL other offerings
  for (const offeringId of Object.keys(allOfferings)) {
    const offering = allOfferings[offeringId];
    const pkg = offering?.availablePackages?.find((p: any) => p.identifier === packageId);
    if (pkg) {
      console.log(`RevenueCat: Found package "${packageId}" in offering "${offeringId}"`);
      return { pkg, offeringId };
    }
  }

  console.warn(`RevenueCat: Package "${packageId}" not found in any offering`);
  return null;
};

/**
 * Get a package from the current offering by its identifier
 * @deprecated Use findPackageAcrossAllOfferings for better coverage
 */
export const getPackageByIdentifier = (packageId: string): PurchasesPackage | null => {
  if (!currentOffering) {
    console.warn('RevenueCat: No current offering available');
    return null;
  }

  const allPackages = currentOffering.availablePackages || [];
  const pkg = allPackages.find((p: any) => p.identifier === packageId);

  if (!pkg) {
    console.warn(`RevenueCat: Package ${packageId} not found in current offering`);
  }

  return pkg || null;
};

/**
 * Purchase a package by its RevenueCat package identifier
 * Now searches across ALL offerings, not just current
 */
export const purchasePackageByIdentifier = async (packageId: string): Promise<CustomerInfo | null> => {
  if (!isNativePlatform()) {
    throw new Error('RevenueCat not available on this platform');
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases || !isInitialized) {
    throw new Error('RevenueCat not initialized');
  }

  // Refresh offerings cache if empty
  if (!cachedOfferings) {
    try {
      const offerings = await Purchases.getOfferings();
      cachedOfferings = offerings;
      currentOffering = offerings?.current || null;
    } catch (error) {
      console.error('RevenueCat: Failed to fetch offerings for purchase', error);
      throw new Error(`Cannot fetch offerings: ${normalizeRevenueCatError(error).message}`);
    }
  }

  // Search across ALL offerings (not just current)
  const result = findPackageAcrossAllOfferings(packageId);

  if (!result) {
    // Log available packages for debugging
    const availablePackages: string[] = [];
    if (cachedOfferings?.all) {
      Object.keys(cachedOfferings.all).forEach(offeringId => {
        const offering = cachedOfferings.all[offeringId];
        offering?.availablePackages?.forEach((pkg: any) => {
          availablePackages.push(`${offeringId}/${pkg.identifier}`);
        });
      });
    }
    console.error(`RevenueCat: Package "${packageId}" not found. Available: ${availablePackages.join(', ') || 'none'}`);
    throw new Error(`Package "${packageId}" not found in any offering. Available packages: ${availablePackages.length > 0 ? availablePackages.join(', ') : 'No packages available - check RevenueCat Dashboard configuration'}`);
  }

  return await purchasePackage(result.pkg);
};

/**
 * Open platform-specific subscription management
 */
export const openSubscriptionManagement = async (): Promise<void> => {
  if (!isNativePlatform()) {
    console.log('Subscription management not available on web');
    return;
  }

  try {
    if (isIOS()) {
      await Browser.open({ url: 'https://apps.apple.com/account/subscriptions' });
    } else if (isAndroid()) {
      await Browser.open({ url: 'https://play.google.com/store/account/subscriptions' });
    }
  } catch (error) {
    console.error('Failed to open subscription management', error);
    throw error;
  }
};

/**
 * Add listener for customer info changes
 */
export const addCustomerInfoListener = async (
  callback: (customerInfo: CustomerInfo) => void
): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  const loaded = await loadRevenueCatSDK();
  if (!loaded || !Purchases) {
    return;
  }

  Purchases.addCustomerInfoUpdateListener(callback);
};

/**
 * Check if RevenueCat is initialized and ready
 */
export const isRevenueCatReady = (): boolean => {
  // SDK is ready if it was configured successfully, even if offerings haven't loaded yet
  return isNativePlatform() && isInitialized && !initializationError;
};

/**
 * Get initialization error if any
 */
export const getRevenueCatError = (): Error | null => {
  // Return init error first (more critical), then offerings error
  return initializationError || offeringsError;
};

/**
 * Check if RevenueCat is currently initializing
 */
export const isRevenueCatInitializing = (): boolean => {
  return isInitializing;
};

/**
 * Force re-initialization (useful after errors)
 */
export const reinitializeRevenueCat = async (userId?: string): Promise<void> => {
  // Reset semaphore explicitly to prevent stuck state on Android
  isInitializing = false;
  isInitialized = false;
  initializationError = null;
  offeringsError = null;
  cachedOfferings = null;
  currentOffering = null;

  // Attempt to clean up existing session
  if (Purchases) {
    try {
      await Purchases.logOut();
    } catch (e) {
      // Ignore logout errors during reinitialization
      console.log('RevenueCat: logOut during reinit (ignored):', e);
    }
  }

  await initializeRevenueCat(userId);
};

/**
 * Sync pending purchases to finish any unacknowledged consumables.
 * Safe and idempotent — does nothing if there are no pending transactions.
 * Can be called preventively (e.g., when entering the credits purchase page).
 */
export const syncPendingPurchases = async (): Promise<void> => {
  if (!isNativePlatform() || !Purchases || !isInitialized) {
    return;
  }

  try {
    console.log('RevenueCat: Syncing pending purchases (manual)...');
    await Purchases.syncPurchases();
    console.log('RevenueCat: Pending purchases synced successfully');
  } catch (error) {
    console.warn('RevenueCat: Failed to sync pending purchases (non-critical):', error);
  }
};

/**
 * Get all cached offerings (for diagnostic purposes)
 */
export const getCachedOfferings = (): PurchasesOfferings | null => {
  return cachedOfferings;
};

/**
 * Get current offering (for diagnostic purposes)
 */
export const getCurrentOffering = (): any | null => {
  return currentOffering;
};
